import { useCallback, useEffect, useState } from "react";
import vitalsTrackingService from "@/services/vitals-tracking.service";
import {
  TrackingConfig,
  RecordReadingDto,
  VitalsReading,
  UpdateReadingDto,
  UpdateRangeDto,
  VitalsParameter,
  VitalsTrackingDto,
} from "@/types/vitals";

/**
 * useVitalsTracking
 *
 * React hook for managing member vitals tracking configuration and readings.
 * Auto-fetches tracking config on mount using memberId dependency.
 *
 * @param memberId - The member ID to fetch and manage vitals for
 *
 * @returns Object with:
 *   - config: Current tracking configuration (null on initial load)
 *   - isLoading: Whether config is being fetched
 *   - error: Error message if config fetch fails
 *   - enableTracking: Enable tracking with initial readings
 *   - recordReading: Record a new vital reading
 *   - updateReading: Update an existing vital reading
 *   - deleteReading: Delete a vital reading
 *   - updateCustomRange: Update custom ranges for a parameter
 *   - disableTracking: Disable tracking for specific parameters
 */
export function useVitalsTracking(memberId: string) {
  const [config, setConfig] = useState<TrackingConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const configData = await vitalsTrackingService.getTrackingConfig(memberId);
      setConfig(configData);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to fetch vitals tracking config");
    } finally {
      setIsLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    if (memberId) fetchConfig();
  }, [memberId, fetchConfig]);

  /**
   * D-06: Action methods wrapped in useCallback
   * All throw errors to caller (do NOT auto-refetch config per D-05)
   */

  const enableTracking = useCallback(
    async (
      enabledParameters: VitalsParameter[],
      initialReadings: Partial<Record<VitalsParameter, Record<string, unknown>>>
    ): Promise<VitalsTrackingDto> => {
      return vitalsTrackingService.enableTracking(memberId, enabledParameters, initialReadings);
    },
    [memberId]
  );

  const recordReading = useCallback(
    async (readingDto: RecordReadingDto): Promise<VitalsReading> => {
      return vitalsTrackingService.recordReading(memberId, readingDto);
    },
    [memberId]
  );

  const updateReading = useCallback(
    async (
      readingId: string,
      updateDto: UpdateReadingDto
    ): Promise<VitalsReading> => {
      return vitalsTrackingService.updateReading(
        memberId,
        readingId,
        updateDto
      );
    },
    [memberId]
  );

  const deleteReading = useCallback(
    async (readingId: string): Promise<void> => {
      return vitalsTrackingService.deleteReading(memberId, readingId);
    },
    [memberId]
  );

  const updateCustomRange = useCallback(
    async (rangeDto: UpdateRangeDto): Promise<VitalsTrackingDto> => {
      return vitalsTrackingService.updateCustomRange(memberId, rangeDto);
    },
    [memberId]
  );

  const disableTracking = useCallback(
    async (parameters: VitalsParameter[]): Promise<VitalsTrackingDto> => {
      return vitalsTrackingService.disableTracking(memberId, parameters);
    },
    [memberId]
  );

  return {
    config,
    isLoading,
    error,
    refreshConfig: fetchConfig,
    enableTracking,
    recordReading,
    updateReading,
    deleteReading,
    updateCustomRange,
    disableTracking,
  };
}
